import React, { useContext, useEffect, useMemo, useState } from "react";
import { Form, Button, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Context } from "..";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

type DraftUser = {
  name: string;
  surname: string;
  departmentId: string;
};

const emptyUser = (): DraftUser => ({
  name: "",
  surname: "",
  departmentId: "",
});

const AddUsers = () => {
  const { store } = useContext(Context);
  const navigate = useNavigate();

  const [users, setUsers] = useState<DraftUser[]>([emptyUser()]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    store.fetchDepartments();
  }, [store]);

  const canSubmit = useMemo(() => {
    return users.every(
      (u) => u.name.trim().length > 0 && u.departmentId.trim().length > 0
    );
  }, [users]);

  const updateUser = (idx: number, patch: Partial<DraftUser>) => {
    setUsers((prev) =>
      prev.map((u, i) => (i === idx ? { ...u, ...patch } : u))
    );
  };

  const addRow = () => setUsers((prev) => [...prev, emptyUser()]);
  const removeRow = (idx: number) =>
    setUsers((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) {
      toast.warn("Заполните минимум: Имя и Отдел у каждого пользователя.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        users: users.map((u) => ({
          name: u.name.trim(),
          surname: u.surname.trim(),
          departmentId: u.departmentId,
        })),
      };

      const res = await store.createDraftUsers(payload);

      const created = res?.created?.length || 0;
      const errors = res?.errors?.length || 0;

      if (created) toast.success(`Создано пользователей: ${created}`);
      if (errors) toast.error(`Ошибок: ${errors} (см. консоль)`);
      if (errors) console.log("bulk-draft errors:", res.errors);

      store.fetchUsers();
      navigate("/users");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || "Ошибка");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>Добавление пользователей (пакетом)</h2>

      <Form onSubmit={handleSubmit}>
        {users.map((u, idx) => (
          <Card className="mb-3" key={idx}>
            <Card.Body>
              <Row className="g-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Имя *</Form.Label>
                    <Form.Control
                      value={u.name}
                      onChange={(e) =>
                        updateUser(idx, { name: e.target.value })
                      }
                      placeholder="Иван"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Фамилия</Form.Label>
                    <Form.Control
                      value={u.surname}
                      onChange={(e) =>
                        updateUser(idx, { surname: e.target.value })
                      }
                      placeholder="Петров"
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Отдел *</Form.Label>
                    <Form.Select
                      value={u.departmentId}
                      onChange={(e) =>
                        updateUser(idx, { departmentId: e.target.value })
                      }
                      required
                    >
                      <option value="">Выберите отдел</option>
                      {store.department.map((d) => (
                        <option key={d._id} value={d._id}>
                          {d.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-3">
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={addRow}
                >
                  Добавить ещё
                </Button>

                {users.length > 1 && (
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={() => removeRow(idx)}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        ))}

        <div className="d-flex gap-2 align-items-center">
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit || submitting}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" /> Сохранение...
              </>
            ) : (
              "Сохранить"
            )}
          </Button>

          <Link className="btn btn-secondary" to="/users">
            Отмена
          </Link>
        </div>
      </Form>
    </div>
  );
};

export default observer(AddUsers);
